import { useQuery } from '@tanstack/react-query'
import { Modal, Table, Input, AutoComplete, Select } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchMastersReportDetail, fetchItemsSelect } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

import { http } from '../../services/http';
import ExcelButton from '../ExcelButton'

	const fetchServices = async ({ queryKey }) => {
		const [_,] = queryKey
		try {
			const url = `api/items/sevices?categories=101&pageSize=1000&skip=0`
			const { data } = await http.get(url)
			return data
		} catch (error) {
			console.log(error)
		}
	}


const MastersReportDetailModal = ({ visible, onClose, cardCode, cardName, initialDateFrom, initialDateTo }) => {
	const { t } = useTranslation()
	const [dateFrom, setDateFrom] = useState(initialDateFrom || '')
	const [dateTo, setDateTo] = useState(initialDateTo || '')
	const [itemCode, setItemCode] = useState('')
	const [itemName, setItemName] = useState('')
	const [salesInvoiceDocNum, setSalesInvoiceDocNum] = useState('')
	const [miqdoriMin, setMiqdoriMin] = useState('')
	const [miqdoriMax, setMiqdoriMax] = useState('')
	const [lineTotalMin, setLineTotalMin] = useState('')
	const [lineTotalMax, setLineTotalMax] = useState('')

	const debouncedItemCode = useDebounce(itemCode, 500)
	const debouncedItemName = useDebounce(itemName, 500)
	const debouncedDocNum = useDebounce(salesInvoiceDocNum, 500)
	const debouncedMiqdoriMin = useDebounce(miqdoriMin, 500)
	const debouncedMiqdoriMax = useDebounce(miqdoriMax, 500)
	const debouncedLineTotalMin = useDebounce(lineTotalMin, 500)
	const debouncedLineTotalMax = useDebounce(lineTotalMax, 500)

	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)

	const { data: itemsData = [], isLoading: itemsLoading } = useQuery({
		queryKey: ['items-select', ],
		queryFn: fetchServices,
		enabled: visible,
	})

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'masters-report-detail',
			pageSize,
			page,
			dateFrom,
			dateTo,
			cardCode,
			cardName,
			debouncedItemCode,
			debouncedItemName,
			debouncedDocNum,
			debouncedMiqdoriMin,
			debouncedMiqdoriMax,
			debouncedLineTotalMin,
			debouncedLineTotalMax,
		],
		queryFn: fetchMastersReportDetail,
		enabled: visible && !!cardCode,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/masters-report-detail/export',
			params: {
				dateFrom,
				dateTo,
				cardCode,
				cardName,
				itemCode: debouncedItemCode,
				itemName: debouncedItemName,
				salesInvoiceDocNum: debouncedDocNum,
				miqdoriMin: debouncedMiqdoriMin,
				miqdoriMax: debouncedMiqdoriMax,
				lineTotalMin: debouncedLineTotalMin,
				lineTotalMax: debouncedLineTotalMax,
				page: 0,
				pageSize: 1000,
			},
			fileName: `${t('details')} - ${cardName}`,
			onLoadingChange: setLoadingExcel,
		})
	}

	const columns = [
		{
			title: t('date'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (val) => val ? moment(val).format('DD.MM.YYYY') : '-',
		},
		{
			title: t('docNum'),
			dataIndex: 'salesInvoiceDocNum',
			key: 'salesInvoiceDocNum',
		},

		{
			title: t('serviceName'),
			dataIndex: 'dscription',
			key: 'dscription',
		},
		{
			title: t('quantity'),
			dataIndex: 'miqdori',
			key: 'miqdori',
			render: (val) => numberWithSpacesIntl(val || 0),
		},
		{
			title: t('price'),
			dataIndex: 'narxi',
			key: 'narxi',
			render: (val) => numberWithSpacesIntl(val || 0),
		},
		{
			title: t('lineTotal'),
			dataIndex: 'lineTotal',
			key: 'lineTotal',
			render: (val) => numberWithSpacesIntl(val || 0),
		},
	]

	return (
		<Modal
			title={`${t('details')} - ${cardName}`}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={1400}
		>
			<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4 mb-6">
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('startDate')}</p>
					<Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0) }} />
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('endDate')}</p>
					<Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0) }} />
				</div>


				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('serviceName')}</p>
					<Select
					filterOption={(input, option) =>
						option.label
							.toLowerCase()
							.includes(input.toLowerCase())
					}						value={itemName}
						options={itemsData?.map(item => ({ value: item.itemName, label: item.itemName }))}
						onChange={(val) => { setItemName(val); setPage(0) }}
						placeholder={t('itemName')}
						className="w-full"
						showSearch
						allowClear
						loading={itemsLoading}
					/>
				</div>

				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('docNum')}</p>
					<Input value={salesInvoiceDocNum} onChange={(e) => { setSalesInvoiceDocNum(e.target.value); setPage(0) }} />
				</div>

				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('minQuantity')}</p>
					<Input value={miqdoriMin} onChange={(e) => { setMiqdoriMin(e.target.value); setPage(0) }} />
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('maxQuantity')}</p>
					<Input value={miqdoriMax} onChange={(e) => { setMiqdoriMax(e.target.value); setPage(0) }} />
				</div>

				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('minLineTotal')}</p>
					<Input value={lineTotalMin} onChange={(e) => { setLineTotalMin(e.target.value); setPage(0) }} />
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-nunito">{t('maxLineTotal') || t('maxTotal')}</p>
					<Input value={lineTotalMax} onChange={(e) => { setLineTotalMax(e.target.value); setPage(0) }} />
				</div>

			</div>
			<div className="flex justify-between items-center mb-4">
				<ExcelButton
					handleDownloadExcel={handleDownloadExcel}
					loadingExcel={loadingExcel}
				/>
				<div className="mt-2 flex sm:mr-10">
					<button
						onClick={() => setPage((prev) => Math.max(0, prev - 1))}
						disabled={page === 0}
						className="h-[30px] w-[110px] bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 "
					>
						{t('previous')}
					</button>
					<div className="bg-[#099204]  w-[20px] h-[30px] flex items-center  justify-center">
						<span className="text-center text-white">{page + 1}</span>
					</div>
					<button
						onClick={() => setPage((prev) => prev + 1)}
						disabled={data.length < pageSize}
						className="h-[30px] w-[110px] bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 "
					>
						{t('next')}
					</button>
				</div>
			</div>
			<Table
				dataSource={data}
				columns={columns}
				loading={isLoading}
				pagination={false}
				rowKey={(record, index) => record.docEntry || index}
				scroll={{ x: 'max-content' }}
			/>
		</Modal>
	)
}

export default MastersReportDetailModal
